import { createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../store";
import {
    IAgeLevel,
    IField,
    IPhoto,
    IPosition,
    ISeason,
    ISiteSetting,
    ISponsor,
    ITickerGame,
    IUser,
} from "../../types/api";
import { IStateDefaults } from "../../types/shared";
import { authActions } from "../auth/authSlice";
import {
    createSponsor,
    deleteSponsor,
    getAgeLevels,
    getFields,
    getPhotos,
    getPositions,
    getSeasons,
    getSettings,
    getSponsors,
    getTickerGames,
    getUmpires,
    updateSponsor,
    updateSponsorOrder,
} from "./globalActions";

export interface GlobalState extends IStateDefaults {
    ageLevels: IAgeLevel[];
    leagueAgeLevels: IAgeLevel[];
    umpires: Array<{ id: IUser["id"]; name: string }>;
    settings: ISiteSetting[];
    fields: IField[];
    seasons: ISeason[];
    positions: IPosition[];
    homePage: {
        selectedYear?: number | undefined;
        selectedSeason?: string | undefined;
        selectedAgeLevel?: string | undefined;
        selectedLeaguePage?: number;
        photos?: IPhoto[];
    };
    sponsors: ISponsor[];
    ticker: {
        games: ITickerGame[];
        date: string | null;
        loading: boolean;
        error: boolean;
    };
}

const initialState: GlobalState = {
    loading: false,
    success: false,
    ageLevels: [],
    leagueAgeLevels: [],
    umpires: [],
    settings: [],
    fields: [],
    seasons: [],
    positions: [],
    homePage: {
        selectedYear: undefined,
        selectedAgeLevel: undefined,
        selectedSeason: undefined,
        selectedLeaguePage: 1,
        photos: [],
    },
    sponsors: [],
    ticker: {
        games: [],
        date: null,
        loading: false,
        error: false,
    },
};

const globalSlice = createSlice({
    name: "global",
    initialState,
    reducers: {
        setSelectedYear: (state, action) => {
            state.homePage.selectedYear = action.payload;
        },
        setSelectedSeason: (state, action) => {
            state.homePage.selectedSeason = action.payload;
        },
        setSelectedAgeLevel: (state, action) => {
            state.homePage.selectedAgeLevel = action.payload;
        },
        setSelectedLeaguePage: (state, action) => {
            state.homePage.selectedLeaguePage = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.umpires = [];
            })
            .addCase(getAgeLevels.fulfilled, (state, { payload }) => {
                state.ageLevels = payload.ageLevels.map(
                    ({ id, ageLevel }) => ({ id, ageLevel }),
                );
                state.leagueAgeLevels = payload.ageLevels
                    .filter(a => a.hasLeague === 1)
                    .map(({ id, ageLevel }) => ({ id, ageLevel }));
            })
            .addCase(getUmpires.fulfilled, (state, { payload }) => {
                state.umpires = payload.umpires;
            })
            .addCase(getSettings.fulfilled, (state, { payload }) => {
                state.settings = payload;
            })
            .addCase(getFields.fulfilled, (state, { payload }) => {
                state.fields = payload;
            })
            .addCase(getSeasons.fulfilled, (state, { payload }) => {
                state.seasons = payload;
            })
            .addCase(getPositions.fulfilled, (state, { payload }) => {
                state.positions = payload;
            })
            .addCase(getPhotos.fulfilled, (state, { payload }) => {
                state.homePage.photos = payload;
            })
            .addCase(getSponsors.fulfilled, (state, { payload }) => {
                state.sponsors = payload;
            })
            .addCase(updateSponsor.fulfilled, (state, { payload }) => {
                state.sponsors = state.sponsors.map(s => {
                    return s.id === payload.id ? payload : s;
                });
            })
            .addCase(createSponsor.fulfilled, (state, { payload }) => {
                state.sponsors.push(payload);
            })
            .addCase(deleteSponsor.fulfilled, (state, { payload }) => {
                state.sponsors = state.sponsors.filter(s => s.id !== payload);
            })
            .addCase(updateSponsorOrder.fulfilled, (state, { payload }) => {
                state.sponsors = state.sponsors.map(s => {
                    const updated = payload.find(p => p.id === s.id);
                    return updated ? { ...s, sortOrder: updated.sortOrder } : s;
                });
            })
            .addCase(getTickerGames.pending, state => {
                state.ticker.loading = true;
                state.ticker.error = false;
            })
            .addCase(getTickerGames.fulfilled, (state, { payload }) => {
                state.ticker.games = payload.games;
                state.ticker.date = payload.date;
                state.ticker.loading = false;
                state.ticker.error = false;
            })
            .addCase(getTickerGames.rejected, state => {
                state.ticker.loading = false;
                state.ticker.error = true;
            });
    },
});

export const globalActions = {
    ...globalSlice.actions,
    getAgeLevels: getAgeLevels,
    getUmpires: getUmpires,
    getSettings: getSettings,
    getFields: getFields,
    getSeasons: getSeasons,
    getPositions: getPositions,
    getPhotos: getPhotos,
    getSponsors: getSponsors,
    updateSponsor: updateSponsor,
    updateSponsorOrder: updateSponsorOrder,
    createSponsor: createSponsor,
    deleteSponsor: deleteSponsor,
    getTickerGames: getTickerGames,
};

export default globalSlice.reducer;

export const selectAgeLevels = createSelector(
    [(state: RootState) => state.global],
    global => global.ageLevels,
);

export const selectLeagueAgeLevels = createSelector(
    [(state: RootState) => state.global],
    global => global.leagueAgeLevels,
);

export const selectSetting = createSelector(
    [
        (state: RootState) => state.global.settings,
        (state: RootState, settingName: string) => settingName,
    ],
    (settings, settingName) =>
        settings.find(setting => setting.settingName === settingName),
);

export const selectAllSettings = createSelector(
    [(state: RootState) => state.global],
    global => global.settings,
);

export const selectFields = createSelector(
    [(state: RootState) => state.global],
    global => global.fields,
);

export const selectUmpires = createSelector(
    [(state: RootState) => state.global],
    global => global.umpires,
);

export const selectSeasons = createSelector(
    [(state: RootState) => state.global],
    global => global.seasons,
);

export const selectPositions = createSelector(
    [(state: RootState) => state.global],
    global => global.positions,
);

export const selectSponsors = createSelector(
    [(state: RootState) => state.global],
    global => [...global.sponsors].sort((a, b) => a.sortOrder - b.sortOrder),
);

export const selectSponsor = createSelector(
    [
        (state: RootState) => state.global.sponsors,
        (state: RootState, sponsorId: number) => sponsorId,
    ],
    (sponsors, sponsorId) => sponsors.find(sponsor => sponsor.id === sponsorId),
);

export const selectTickerGames = createSelector(
    [(state: RootState) => state.global.ticker],
    ticker => ticker.games,
);

export const selectTickerDate = createSelector(
    [(state: RootState) => state.global.ticker],
    ticker => ticker.date,
);

export const selectTickerLoading = createSelector(
    [(state: RootState) => state.global.ticker],
    ticker => ticker.loading,
);
